import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { api } from "./api.js";
import { Layout, Loading } from "./components/Layout.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { WorkoutPage } from "./pages/WorkoutPage.js";
import { GymNative, isNativeAndroid } from "./native.js";
import type { Workout } from "./types.js";
import { findNextWorkoutSet } from "./workout-notification.js";

const BodyPage = lazy(() => import("./pages/BodyPage.js").then((module) => ({ default: module.BodyPage })));
const GoalsPage = lazy(() => import("./pages/GoalsPage.js").then((module) => ({ default: module.GoalsPage })));
const HistoryPage = lazy(() => import("./pages/HistoryPage.js").then((module) => ({ default: module.HistoryPage })));
const ExerciseHistoryPage = lazy(() => import("./pages/ExerciseHistoryPage.js").then((module) => ({ default: module.ExerciseHistoryPage })));

export default function App() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [androidUpdate, setAndroidUpdate] = useState<string | null>(null);
  useEffect(() => { void api.session().then((result) => setAuthenticated(result.authenticated)).catch(() => setAuthenticated(false)); }, []);
  useEffect(() => {
    if (!isNativeAndroid) return;
    void Promise.all([api.get<{ latestVersion: string }>("/api/mobile/version"), GymNative.getAppInfo()])
      .then(([release, installed]) => setAndroidUpdate(release.latestVersion !== installed.versionName ? release.latestVersion : null))
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!isNativeAndroid || !authenticated) return;
    let disposed = false;
    void api.get<Workout[]>("/api/workouts?limit=1&status=in_progress").then(async ([active]) => {
      if (disposed) return;
      if (!active) {
        await GymNative.clearActiveWorkout();
        return;
      }
      const permissions = await GymNative.requestNotificationPermission();
      if (disposed || !permissions.notifications) return;
      const next = findNextWorkoutSet(active);
      await GymNative.syncActiveWorkout({
        workoutId: active.id,
        workoutTitle: active.title,
        nextExerciseName: next?.exerciseName ?? null,
        nextSetNumber: next?.setNumber ?? 0,
        nextSetDetails: next?.details ?? null
      });
    }).catch(() => undefined);
    return () => { disposed = true; };
  }, [authenticated]);
  if (authenticated === null) return <Loading />;
  if (!authenticated) return <LoginPage onAuthenticated={() => setAuthenticated(true)} />;
  const logout = async () => { await api.logout(); setAuthenticated(false); };
  return <BrowserRouter><Layout onLogout={() => void logout()} androidUpdate={androidUpdate}><Suspense fallback={<Loading />}><Routes>
    <Route path="/" element={<DashboardPage />} />
    <Route path="/workout/:id" element={<WorkoutPage />} />
    <Route path="/history" element={<HistoryPage />} />
    <Route path="/exercise-history" element={<ExerciseHistoryPage />} />
    <Route path="/body" element={<BodyPage />} />
    <Route path="/goals" element={<GoalsPage />} />
  </Routes></Suspense></Layout></BrowserRouter>;
}
